import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { faCalendarWeek } from '@fortawesome/pro-regular-svg-icons/faCalendarWeek';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Col, Row, type TableColumnsType } from 'antd';
import { PosCard } from 'pos-client/components/shared/Display/Card/PosCard.tsx';
import { PosTable } from 'pos-client/components/shared/Display/Table/PosTable.tsx';
import { useTableData } from 'pos-client/hooks/useTableData.ts';
import { CreateEditScheduleLanesModal } from 'pos-client/pages/locations/Modals/CreateEditScheduleLanesModal.tsx';
import {
  createTableFilters,
  filterByProperty,
  sortDates,
  sortWeekdays,
} from 'pos-client/utils/dataTableHelpers.tsx';
import { convertISOStringToLocalDate } from 'pos-client/utils/dateHelpers.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import type { ScheduleLaneModel } from 'pos-shared/models/openingHoursGroupSchema.ts';
import { getDateFromISO } from 'pos-shared/utils/dateTimeHelpers.ts';

export const DefaultSchedule = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalData, setModalData] = useState<ScheduleLaneModel>();
  const { data, isLoading } = trpc.getAllScheduleLanes.useQuery({
    id: id as string,
  });
  const tableData = useTableData(data, (x) => x.id);

  const formattedTableData = tableData.map((obj) => ({ ...obj }));
  formattedTableData.forEach((element) => {
    element.dateFrom = getDateFromISO(element.dateFrom);
    element.dateTo = getDateFromISO(element.dateTo);
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: TableColumnsType<any> = [
    {
      title: t('common.time.unit.day'),
      dataIndex: 'dayOfTheWeek',
      key: 'dayOfTheWeek',
      sorter: (a, b) => sortWeekdays<ScheduleLaneModel>(a, b, 'dayOfTheWeek'),
      filters: createTableFilters(tableData, 'dayOfTheWeek'),
      onFilter: (value, record) =>
        filterByProperty(value, record, 'dayOfTheWeek'),
    },
    {
      title: t('common.date.from'),
      dataIndex: 'dateFrom',
      key: 'dateFrom',
      render: (_, record) => convertISOStringToLocalDate(record.dateFrom),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sorter: (a, b) => sortDates<any>(a, b, 'dateFrom'),
      filters: createTableFilters(formattedTableData, 'dateFrom', true),
      onFilter: (value, record) => filterByProperty(value, record, 'dateFrom'),
    },
    {
      title: t('common.date.to'),
      dataIndex: 'dateTo',
      key: 'dateTo',
      render: (_, record) => convertISOStringToLocalDate(record.dateTo),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sorter: (a, b) => sortDates<any>(a, b, 'dateTo'),
      filters: createTableFilters(formattedTableData, 'dateTo', true),
      onFilter: (value, record) => filterByProperty(value, record, 'dateTo'),
    },
    {
      title: '',
      dataIndex: 'id',
      width: 10,
      align: 'center',
      render: (_, data) => (
        <Button size="small" onClick={() => showEditModal(data)}>
          {t('common.buttons.edit')}
        </Button>
      ),
    },
  ];

  const showModal = () => {
    setIsModalVisible(true);
  };

  const showEditModal = (data: ScheduleLaneModel) => {
    setModalData(data);
    setIsModalVisible(true);
  };

  const handleOk = () => {
    setIsModalVisible(false);
    setModalData(undefined);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setModalData(undefined);
  };

  return (
    <Col>
      <PosCard
        headerProps={{
          title: t('scheduling.defaultSchedule'),
          icon: faCalendarWeek,
          action: (
            <Button
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={showModal}
            >
              {t('common.buttons.addNew')}
            </Button>
          ),
        }}
      >
        <Row>
          <Col flex="auto">
            <PosTable
              id="defaultScheduleDetailsTable"
              columns={columns}
              dataSource={formattedTableData}
              isLoading={isLoading}
              fePagination
            />
          </Col>
        </Row>
        {isModalVisible && (
          <CreateEditScheduleLanesModal
            handleOk={handleOk}
            handleCancel={handleCancel}
            data={modalData}
            tableData={data}
          />
        )}
      </PosCard>
    </Col>
  );
};
